import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { Box, Flex } from 'reflexbox';

import { CycleMoment } from '@treams/treams-analytics';
import { useLocalize } from '@treams/treams-hooks';
import { SendIcon } from '@treams/treams-icons';
import { MomentType } from '@treams/treams-network';
import {
	Button,
	CYCLE_MOMENT_ROW_HEIGHT,
	type CycleMomentBarStatus,
	CycleMomentOffset,
	CycleMomentsCard,
	Heading3,
	Paragraph,
	Separator,
	Tooltip,
} from '@treams/treams-ui';

import PopoverTarget from '../../../packages/kakapo-ui-kit/components/popovers/PopoverTarget';
import { MomentIcon } from './MomentIcon';
import { Manager } from './RemindManagersModal';

type WithDates = { startDate: Date; endDate: Date };

function momentBarStatus(cycleMoment: CycleMoment & WithDates): CycleMomentBarStatus {
	if (moment(cycleMoment.endDate).isBefore(moment())) {
		return 'completed';
	}

	return 'upcoming';
}

function CycleMomentTooltipContent({
	cycleMoment,
	managersToRemind,
	onRemind,
}: {
	cycleMoment: CycleMoment & WithDates;
	managersToRemind: Manager[];
	onRemind: (managers: Manager[]) => void;
}) {
	const { t } = useTranslation();
	const { localize } = useLocalize();

	const isMessageMoment = cycleMoment.type === MomentType.Message;
	const isNotStarted = moment(cycleMoment.startDate).isAfter(moment());

	const reminderDisabled = isMessageMoment || isNotStarted || managersToRemind.length === 0;

	const reminderWarning = isMessageMoment
		? t('analytics.cycles.moments.remindMessageWarning')
		: isNotStarted
			? t('analytics.cycles.moments.remindNotStartedWarning')
			: t('analytics.cycles.managers.remindNoneWarning');

	return (
		<Box p={3} width={260}>
			<Flex alignItems="center">
				<Box size={16} mr={2} flexShrink={0}>
					<MomentIcon momentType={cycleMoment.type} size={16} />
				</Box>
				<Heading3>{localize(cycleMoment.title).text}</Heading3>
			</Flex>
			<Paragraph small mt={1} mb={0}>
				{moment(cycleMoment.startDate).format('LL')} – {moment(cycleMoment.endDate).format('LL')}
			</Paragraph>

			<Separator my={3} />

			<Flex justifyContent="center">
				<Tooltip enabled={reminderDisabled} content={reminderWarning}>
					<Button
						icon={<SendIcon />}
						title={t('analytics.cycles.managers.remindAll')}
						type="primary"
						disabled={reminderDisabled}
						onClick={() => onRemind(managersToRemind)}
						small
					/>
				</Tooltip>
			</Flex>
		</Box>
	);
}

function AnalyticsMomentPopovers({
	bounds,
	moments,
	getManagersToRemind,
	onRemind,
}: {
	bounds: [Date, Date];
	moments: (CycleMoment & WithDates)[];
	getManagersToRemind: (momentID: string) => Manager[];
	onRemind: (managers: Manager[]) => void;
}) {
	return (
		<>
			{moments.map((cycleMoment) => (
				<Box key={cycleMoment.id}>
					<CycleMomentOffset
						bounds={bounds}
						startDate={cycleMoment.startDate}
						endDate={cycleMoment.endDate}
					>
						<PopoverTarget
							content={
								<CycleMomentTooltipContent
									cycleMoment={cycleMoment}
									managersToRemind={getManagersToRemind(cycleMoment.id)}
									onRemind={onRemind}
								/>
							}
						>
							<div style={{ height: CYCLE_MOMENT_ROW_HEIGHT }} />
						</PopoverTarget>
					</CycleMomentOffset>
				</Box>
			))}
		</>
	);
}

export function CycleMomentsTimeline({
	moments,
	startDate,
	endDate,
	getManagersToRemind,
	onRemind,
}: {
	moments: (CycleMoment & WithDates)[];
	startDate: Date;
	endDate: Date;
	getManagersToRemind: (momentID: string) => Manager[];
	onRemind: (managers: Manager[]) => void;
}) {
	const { t } = useTranslation();
	const { localize } = useLocalize();
	const bounds: [Date, Date] = [startDate, endDate];

	return (
		<CycleMomentsCard
			bounds={bounds}
			currentDate={new Date()}
			embedded
			todayLabel={t('common.today')}
			formatHoverDate={(date) => moment(date).format('LL')}
			moments={moments.map((cycleMoment) => ({
				id: cycleMoment.id,
				startDate: cycleMoment.startDate,
				endDate: cycleMoment.endDate,
				title: localize(cycleMoment.title).text ?? '',
				status: momentBarStatus(cycleMoment),
				icon: <MomentIcon momentType={cycleMoment.type} size={20} />,
			}))}
			overlay={
				<AnalyticsMomentPopovers
					bounds={bounds}
					moments={moments}
					getManagersToRemind={getManagersToRemind}
					onRemind={onRemind}
				/>
			}
		/>
	);
}
